import { logger } from '../lib/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Trophy, RotateCcw, ClipboardCheck, Shield, Sparkles, Award, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import QuizShareCard from '../components/QuizShareCard';

const getProgressBarColor = (percentual) => {
  if (percentual >= 70) return 'bg-green-500';
  if (percentual >= 50) return 'bg-yellow-500';
  return 'bg-orange-500';
};

const QuizPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  // Ref para controlar o timer de auto-avanço — evita race conditions quando o
  // usuário clica rapidamente em várias opções/perguntas em sequência.
  const autoAdvanceTimer = useRef(null);

  // Estados
  const [step, setStep] = useState('form'); // form, quiz, result
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    experiencia: '',
    aluno_wing: '',
    mensagem: ''
  });
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quiz/questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      logger.error('Erro ao buscar perguntas:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o quiz.",
        variant: "destructive"
      });
    }
  }, [BACKEND_URL, toast]);

  // Buscar perguntas ao carregar
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/quiz/start`, { ...formData, consentimento: true });
      setAttemptId(response.data.attempt_id);
      setStep('quiz');
      // Persist quiz data to prefill LeadForm later (Waitlist conversion)
      try {
        sessionStorage.setItem(
          'wing_lead_prefill',
          JSON.stringify({
            nome: formData.nome,
            email: formData.email,
            telefone: formData.whatsapp,
            experiencia: formData.experiencia,
            mensagem: formData.mensagem || '',
          })
        );
      } catch (storageErr) {
        logger.warn('Não foi possível salvar pre-fill no sessionStorage:', storageErr);
      }
      toast({
        title: "Vamos lá!",
        description: "Quiz iniciado. Boa sorte!",
      });
    } catch (error) {
      logger.error('Erro ao iniciar quiz:', error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar o quiz. Verifique seus dados.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    // Functional updater: garante que estamos sempre usando o `answers` mais recente.
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));

    // Cancela qualquer auto-avanço pendente para evitar race conditions
    // (usuário clicando rápido em várias respostas em sequência).
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }

    // Auto-avança para a próxima pergunta após pequeno delay (deixa o usuário ver
    // a opção marcada antes de mudar de tela). Na última pergunta, não avança.
    if (currentQuestion < questions.length - 1) {
      autoAdvanceTimer.current = setTimeout(() => {
        setCurrentQuestion((prev) => Math.min(prev + 1, questions.length - 1));
        autoAdvanceTimer.current = null;
      }, 350);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    // Identifica perguntas sem resposta
    const unanswered = questions.find((q) => !answers[q.id]);
    if (unanswered) {
      const idx = questions.findIndex((q) => q.id === unanswered.id);
      toast({
        title: 'Pergunta não respondida',
        description: `Você ainda não respondeu a pergunta ${idx + 1}. Vamos voltar para ela.`,
        variant: 'destructive',
      });
      setCurrentQuestion(idx);
      return;
    }

    const respostas = Object.entries(answers).map(([questionId, resposta]) => ({
      question_id: parseInt(questionId),
      resposta,
    }));

    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quiz/submit`, {
        attempt_id: attemptId,
        respostas
      });
      setResult(response.data);
      setStep('result');
    } catch (error) {
      logger.error('Erro ao enviar quiz:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar o quiz.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestart = () => {
    setStep('form');
    setFormData({ nome: '', email: '', whatsapp: '', experiencia: '', aluno_wing: '', mensagem: '' });
    setAttemptId(null);
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
  };

  // Renderizar formulário de captura
  const renderForm = () => (
    <section className="relative">
      <div className="container mx-auto px-2 sm:px-4">
        <div className="max-w-5xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8 md:mb-12 px-2">
            <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-yellow-500 rounded-full mb-4">
              <ClipboardCheck className="w-7 h-7 md:w-8 md:h-8 text-black" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 md:mb-4 leading-tight">
              Teste Seu <span className="gradient-text">Conhecimento</span> sobre WING
            </h1>
            <p className="text-sm md:text-xl text-gray-400 mb-3 md:mb-4 max-w-2xl mx-auto">
              Descubra o quanto você sabe sobre a Console WING e ganhe um diagnóstico personalizado.
            </p>
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 md:px-6 py-2 rounded-full">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              <span className="text-yellow-400 font-semibold text-xs md:text-sm">10 perguntas • Resultado imediato • Card compartilhável</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            {/* Left: Form */}
            <div className="glass-dark p-5 md:p-8 rounded-2xl border border-yellow-500/20">
              <h2 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6">Preencha seus dados para começar</h2>

              <form onSubmit={handleFormSubmit} className="space-y-4 md:space-y-5">
                <div>
                  <label className="block text-white font-medium mb-2 text-sm md:text-base">Nome completo *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                    placeholder="Seu nome"
                    data-testid="quiz-nome-input"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2 text-sm md:text-base">E-mail *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                    placeholder="seu@email.com"
                    data-testid="quiz-email-input"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2 text-sm md:text-base">WhatsApp *</label>
                  <input
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                    placeholder="(11) 99999-9999"
                    data-testid="quiz-whatsapp-input"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2 text-sm md:text-base">
                    Nível de experiência <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.experiencia}
                    onChange={(e) => setFormData({ ...formData, experiencia: e.target.value })}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors"
                    data-testid="quiz-experiencia-select"
                  >
                    <option value="" className="bg-gray-900">Selecione uma opção</option>
                    <option value="iniciante" className="bg-gray-900">Iniciante - Nunca usei a WING</option>
                    <option value="basico" className="bg-gray-900">Básico - Já tive contato</option>
                    <option value="intermediario" className="bg-gray-900">Intermediário - Uso regularmente</option>
                    <option value="avancado" className="bg-gray-900">Avançado - Quero me especializar</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2 text-sm md:text-base">
                    Já é aluno da WING Academy? <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'sim', label: 'Sim' },
                      { value: 'nao', label: 'Não' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        data-testid={`quiz-aluno-wing-${opt.value}`}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors text-sm md:text-base font-medium ${
                          formData.aluno_wing === opt.value
                            ? 'border-yellow-500 bg-yellow-500/15 text-white'
                            : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="aluno_wing"
                          value={opt.value}
                          checked={formData.aluno_wing === opt.value}
                          onChange={(e) => setFormData({ ...formData, aluno_wing: e.target.value })}
                          required
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2 text-sm md:text-base">
                    Deixe uma breve mensagem sobre você e sua carreira no áudio
                  </label>
                  <textarea
                    value={formData.mensagem}
                    onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                    rows={4}
                    maxLength={2000}
                    placeholder="Conte um pouco sobre sua trajetória, em quais projetos atua, o que busca aprender..."
                    data-testid="quiz-mensagem-textarea"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors resize-y"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !consent}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg transition-all duration-300 hover:scale-[1.02] glow-yellow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="quiz-start-btn"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                      Iniciando...
                    </>
                  ) : (
                    <>
                      COMEÇAR QUIZ
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                {/* LGPD Note */}
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="quiz-consent-label">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      data-testid="quiz-consent-checkbox"
                      className="mt-1 w-4 h-4 accent-yellow-500 flex-shrink-0"
                    />
                    <span className="text-[11px] md:text-xs text-gray-400 leading-relaxed">
                      Li e concordo com a{' '}
                      <a
                        href="/politica-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="quiz-privacy-link"
                        className="text-yellow-400 hover:underline"
                      >
                        Política de Privacidade (LGPD)
                      </a>{' '}
                      e autorizo o contato da WING Academy Brasil por e-mail e WhatsApp.
                    </span>
                  </label>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-gray-400">
                    <Shield className="w-4 h-4 text-yellow-400" />
                    <span>Seus dados estão seguros e protegidos</span>
                  </div>
                </div>
              </form>
            </div>

            {/* Right: Benefits */}
            <div className="space-y-5 md:space-y-6">
              <div className="glass p-5 md:p-6 rounded-xl">
                <h3 className="text-lg md:text-2xl font-bold text-white mb-4">
                  O que você ganha ao fazer o Quiz:
                </h3>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    'Diagnóstico do seu nível atual com a WING',
                    'Resultado imediato com revisão das respostas',
                    'Card personalizado para compartilhar nas redes',
                    'Conteúdos exclusivos no seu e-mail e WhatsApp',
                    'Acesso prioritário às próximas turmas'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Como funciona */}
              <div className="bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/20 p-5 md:p-6 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-black" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-base md:text-lg mb-2">
                      Como funciona
                    </h4>
                    <ul className="space-y-1.5 text-gray-300 text-sm">
                      <li>• 10 perguntas práticas sobre a Console WING</li>
                      <li>• Sem tempo limite — responda no seu ritmo</li>
                      <li>• Pontuação e classificação na hora</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bonus / CTA */}
              <div className="glass p-5 md:p-6 rounded-xl">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm md:text-base">
                    Quem acerta <strong className="text-yellow-400">70% ou mais</strong> ganha selo "Muito Bom" no card de compartilhamento. Topa o desafio?
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-6 md:mt-8">
            <a href="/" data-testid="quiz-back-home" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
              ← Voltar para o site
            </a>
          </div>
        </div>
      </div>
    </section>
  );

  // Renderizar quiz
  const renderQuiz = () => {
    const question = questions[currentQuestion];
    if (!question) return null;

    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-6 md:mb-8">
          <div className="flex justify-between text-xs md:text-sm text-gray-400 mb-2">
            <span>Pergunta {currentQuestion + 1} de {questions.length}</span>
            <span>{answeredCount} respondidas</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div 
              className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="glass-dark p-5 md:p-8 rounded-2xl border border-yellow-500/20 mb-5 md:mb-6">
          <h2 className="text-base md:text-2xl font-bold text-white mb-5 md:mb-8 leading-snug" data-testid="quiz-question">
            {question.pergunta}
          </h2>

          <div className="space-y-3 md:space-y-4">
            {Object.entries(question.opcoes).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleAnswer(question.id, key)}
                className={`w-full text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-300 text-sm md:text-base ${
                  answers[question.id] === key
                    ? 'border-yellow-500 bg-yellow-500/20 text-white'
                    : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/30 hover:bg-white/10'
                }`}
                data-testid={`quiz-option-${key}`}
              >
                <span className={`inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full mr-2 md:mr-3 font-bold text-xs md:text-base ${
                  answers[question.id] === key
                    ? 'bg-yellow-500 text-black'
                    : 'bg-white/10 text-white'
                }`}>
                  {key}
                </span>
                {value}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between gap-3 md:gap-4">
          <button
            onClick={handlePrev}
            disabled={currentQuestion === 0}
            className="flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-6 py-3 rounded-lg bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            Anterior
          </button>

          {currentQuestion === questions.length - 1 ? (
            <button
              onClick={handleSubmitQuiz}
              disabled={isLoading || !answers[question.id]}
              className="flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-6 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
              data-testid="quiz-submit-btn"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                  Finalizar
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-6 py-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black font-bold transition-colors text-sm md:text-base"
            >
              Próxima
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          )}
        </div>

        {/* Question Navigation Dots */}
        <div className="flex justify-center gap-1.5 md:gap-2 mt-6 md:mt-8 flex-wrap">
          {questions.map((q, index) => {
            let dotClass = 'bg-gray-800 text-gray-400 hover:bg-gray-700';
            if (index === currentQuestion) dotClass = 'bg-yellow-500 text-black';
            else if (answers[q.id]) dotClass = 'bg-green-500 text-white';
            return (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(index)}
                className={`w-7 h-7 md:w-8 md:h-8 rounded-full text-xs md:text-sm font-bold transition-all ${dotClass}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Renderizar resultado
  const renderResult = () => {
    if (!result) return null;

    const getGrade = () => {
      if (result.percentual >= 90) return { text: 'EXCELENTE!', color: 'text-green-400', emoji: '🏆' };
      if (result.percentual >= 70) return { text: 'MUITO BOM!', color: 'text-blue-400', emoji: '⭐' };
      if (result.percentual >= 50) return { text: 'BOM!', color: 'text-yellow-400', emoji: '👍' };
      return { text: 'CONTINUE ESTUDANDO!', color: 'text-orange-400', emoji: '📚' };
    };

    const grade = getGrade();

    return (
      <div className="max-w-3xl mx-auto">
        {/* Result Header */}
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-24 md:h-24 bg-yellow-500 rounded-full mb-3 md:mb-4">
            <Trophy className="w-8 h-8 md:w-12 md:h-12 text-black" />
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">Quiz Concluído!</h1>
          <p className={`text-lg md:text-2xl font-bold ${grade.color}`}>
            {grade.emoji} {grade.text}
          </p>
        </div>

        {/* Score Card */}
        <div className="glass-dark p-5 md:p-8 rounded-2xl border border-yellow-500/20 mb-6 md:mb-8 text-center">
          <div className="text-5xl md:text-6xl font-bold text-yellow-400 mb-2" data-testid="quiz-score">
            {result.pontuacao}/{result.total}
          </div>
          <div className="text-lg md:text-2xl text-white mb-4">
            {result.percentual.toFixed(0)}% de acertos
          </div>
          <div className="w-full bg-gray-800 rounded-full h-3 md:h-4 mb-2">
            <div 
              className={`h-3 md:h-4 rounded-full transition-all duration-500 ${getProgressBarColor(result.percentual)}`}
              style={{ width: `${result.percentual}%` }}
            ></div>
          </div>
        </div>

        {/* Share Card */}
        <QuizShareCard
          nome={formData.nome}
          pontuacao={result.pontuacao}
          total={result.total}
          percentual={result.percentual}
        />

        {/* Answers Review */}
        <div className="glass-dark p-4 md:p-6 rounded-2xl border border-white/10 mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6">Revisão das Respostas</h2>
          <div className="space-y-3 md:space-y-4">
            {result.respostas.map((r, index) => {
              const question = questions.find(q => q.id === r.question_id);
              return (
                <div 
                  key={r.question_id}
                  className={`p-3 md:p-4 rounded-lg border ${
                    r.correta 
                      ? 'border-green-500/30 bg-green-500/10' 
                      : 'border-red-500/30 bg-red-500/10'
                  }`}
                >
                  <div className="flex items-start gap-2 md:gap-3">
                    {r.correta ? (
                      <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-400 flex-shrink-0 mt-0.5 md:mt-1" />
                    ) : (
                      <XCircle className="w-5 h-5 md:w-6 md:h-6 text-red-400 flex-shrink-0 mt-0.5 md:mt-1" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm md:text-base font-medium mb-2 leading-snug">
                        {index + 1}. {question?.pergunta}
                      </p>
                      <p className="text-xs md:text-sm">
                        <span className="text-gray-400">Sua resposta: </span>
                        <span className={r.correta ? 'text-green-400' : 'text-red-400'}>
                          {r.resposta}) {question?.opcoes[r.resposta]}
                        </span>
                      </p>
                      {!r.correta && (
                        <p className="text-xs md:text-sm mt-1">
                          <span className="text-gray-400">Resposta correta: </span>
                          <span className="text-green-400">
                            {r.resposta_correta}) {question?.opcoes[r.resposta_correta]}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
          <button
            onClick={handleRestart}
            className="flex-1 flex items-center justify-center gap-2 px-4 md:px-6 py-3 md:py-4 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors text-sm md:text-base"
          >
            <RotateCcw className="w-4 h-4 md:w-5 md:h-5" />
            Fazer Novamente
          </button>
          <a
            href="/"
            className="flex-1 flex items-center justify-center gap-2 px-4 md:px-6 py-3 md:py-4 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black font-bold transition-colors text-sm md:text-base"
          >
            Conhecer a Mentoria
            <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
          </a>
        </div>

        {/* CTA */}
        <div className="mt-6 md:mt-8 p-5 md:p-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-2xl border border-yellow-500/30 text-center">
          <h3 className="text-lg md:text-xl font-bold text-white mb-2">
            Quer dominar a WING de verdade?
          </h3>
          <p className="text-gray-300 text-sm md:text-base mb-4">
            Participe da nossa Mentoria em Grupo e aprenda tudo sobre a console!
          </p>
          <a
            href="/#lead-form"
            className="inline-block w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black px-6 sm:px-8 py-3 rounded-lg font-bold transition-all hover:scale-105"
          >
            QUERO SABER MAIS
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black py-6 md:py-12 px-3 md:px-4">
      {step === 'form' && renderForm()}
      {step === 'quiz' && renderQuiz()}
      {step === 'result' && renderResult()}
    </div>
  );
};

export default QuizPage;
